/**
 * CommissionBreakdownCard Component - Shows detailed commission breakdown
 * Displays base commission, adjustments, and total for transparency
 */

import React, { useState } from 'react';
import {
  Card,
  Box,
  Text,
  InlineStack,
  BlockStack,
  Spinner,
  Badge,
  Tooltip,
  Icon,
  Button,
  Modal,
  DataTable,
  Divider,
} from '@shopify/polaris';
import {
  ArrowUpIcon,
  ArrowDownIcon,
  InfoIcon,
  CashDollarIcon,
  ViewIcon,
} from '@shopify/polaris-icons';
import apiClient from '../../utils/api';

export default function CommissionBreakdownCard({
  commissionBreakdown,
  loading = false,
  error = null,
  change,
  tooltip = "Detailed breakdown of commission calculation including base commission and adjustments",
  color = 'default',
  className = '',
  ...props
}) {
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Color theme matching MetricCard - moved to top to avoid hoisting issues
  const getColorTheme = () => {
    switch (color) {
      case 'success':
        return {
          cardTone: 'success-subdued',
          valueTone: 'success',
        };
      case 'warning':
        return {
          cardTone: 'warning-subdued',
          valueTone: 'warning',
        };
      case 'critical':
        return {
          cardTone: 'critical-subdued',
          valueTone: 'critical',
        };
      default:
        return {
          cardTone: null,
          valueTone: 'base',
        };
    }
  };

  const colorTheme = getColorTheme();

  // Loading state
  if (loading) {
    return (
      <Card {...props} background={colorTheme.cardTone}>
        <Box padding="400" height="200">
          <BlockStack gap="300" align="space-between">
            <InlineStack align="space-between" blockAlign="center">
              <Text variant="bodySm" tone="subdued">
                Commission Breakdown
              </Text>
              <Box>
                <Icon source={CashDollarIcon} tone={colorTheme.valueTone} />
              </Box>
            </InlineStack>

            <BlockStack gap="200">
              <InlineStack gap="200" blockAlign="center">
                <Spinner size="small" />
                <Text variant="bodySm" tone="subdued">Loading breakdown...</Text>
              </InlineStack>
            </BlockStack>
          </BlockStack>
        </Box>
      </Card>
    );
  }

  // Error state
  if (error) {
    return (
      <Card {...props} background={colorTheme.cardTone}>
        <Box padding="400" height="200">
          <BlockStack gap="300" align="space-between">
            <InlineStack align="space-between" blockAlign="center">
              <Text variant="bodySm" tone="subdued">
                Commission Breakdown
              </Text>
              <Tooltip content="Unable to load breakdown data">
                <Icon source={InfoIcon} tone="critical" />
              </Tooltip>
            </InlineStack>

            <Text variant="headingLg" tone="critical">
              --
            </Text>

            <Text variant="bodySm" tone="critical">
              Error loading breakdown
            </Text>
          </BlockStack>
        </Box>
      </Card>
    );
  }

  // Safe data access with defaults
  const breakdown = commissionBreakdown || {
    baseCommission: 0,
    totalAdjustmentAmount: 0,
    totalCommission: 0,
    adjustmentDetails: []
  };

  const {
    baseCommission,
    totalAdjustmentAmount,
    totalCommission,
    adjustmentDetails = []
  } = breakdown;

  // Format currency values
  const formatCurrency = (amount) => {
    try {
      return apiClient.formatCurrency(amount);
    } catch (error) {
      return `$${(amount || 0).toFixed(2)}`;
    }
  };


  // Render change indicator
  const renderChange = () => {
    if (!change) return null;

    const isPositive = change.direction === 'up';
    const isNeutral = change.percentage === 0 || change.direction === 'neutral';

    if (isNeutral) {
      return (
        <Text variant="bodySm" tone="subdued">
          No change {change.period && `from ${change.period}`}
        </Text>
      );
    }

    return (
      <InlineStack gap="100" blockAlign="center">
        <Icon
          source={isPositive ? ArrowUpIcon : ArrowDownIcon}
          tone={isPositive ? 'success' : 'critical'}
        />
        <Text
          variant="bodySm"
          tone={isPositive ? 'success' : 'critical'}
          fontWeight="medium"
        >
          {Math.abs(change.percentage || 0).toFixed(1)}%
        </Text>
        {change.period && (
          <Text variant="bodySm" tone="subdued">
            {change.period}
          </Text>
        )}
      </InlineStack>
    );
  };

  // Build data table for adjustment details modal
  const adjustmentRows = adjustmentDetails.map(adjustment => [
    <Badge tone={adjustment.adjustmentType === 'ADDITION' ? 'success' : 'critical'}>
      {adjustment.adjustmentType === 'ADDITION' ? 'Addition' : 'Deduction'}
    </Badge>,
    adjustment.category,
    adjustment.reason || '--',
    adjustment.brandName || 'Unknown',
    formatCurrency(adjustment.effectiveAmount),
    new Date(adjustment.createdAt).toLocaleDateString(),
    adjustment.createdBy || 'Unknown'
  ]);

  const adjustmentTableHeadings = [
    'Type',
    'Category',
    'Reason',
    'Brand',
    'Amount',
    'Date',
    'Created By'
  ];

  return (
    <>
      <Card {...props} background={colorTheme.cardTone}>
        <Box height="200">
          <BlockStack gap="100" align="space-between">
            {/* Header with title and tooltip */}
            <InlineStack align="space-between" blockAlign="center">
              <InlineStack gap="100" blockAlign="center">
                <Text variant="bodySm" tone="subdued">
                  Commission + Adjustments
                </Text>
                {tooltip && (
                  <Tooltip content={tooltip} preferredPosition="above">
                    <Box paddingInlineStart="100">
                      <Icon source={InfoIcon} tone="subdued" />
                    </Box>
                  </Tooltip>
                )}
              </InlineStack>

              <Box>
                <Icon source={CashDollarIcon} tone={colorTheme.valueTone} />
              </Box>
            </InlineStack>

            {/* Main value - matches MetricCard pattern exactly */}
            <BlockStack gap="100">
              <Text
                variant="headingLg"
                tone="success"
                fontWeight="semibold"
              >
                {formatCurrency(totalCommission)}
              </Text>

              {/* Simple subtitle with breakdown summary */}
              <InlineStack gap="100" blockAlign="center">
                <Text variant="bodySm" tone="subdued">
                  Base: {formatCurrency(baseCommission)}
                  {totalAdjustmentAmount !== 0 && (
                    <>
                      {', Adjustments: '}
                      <Text as="span" tone={totalAdjustmentAmount >= 0 ? 'success' : 'critical'}>
                        {formatCurrency(totalAdjustmentAmount)}
                      </Text>
                    </>
                  )}
                </Text>
                {adjustmentDetails.length > 0 && (
                  <Button
                    variant="plain"
                    size="micro"
                    onClick={() => setShowDetailsModal(true)}
                  >
                    Details
                  </Button>
                )}
              </InlineStack>
            </BlockStack>

            {/* Change indicator */}
            <Box>
              {renderChange()}
            </Box>
          </BlockStack>
        </Box>
      </Card>

      {/* Adjustment Details Modal */}
      {adjustmentDetails.length > 0 && (
        <Modal
          open={showDetailsModal}
          onClose={() => setShowDetailsModal(false)}
          title="Commission Adjustment Details"
          size="large"
        >
          <Modal.Section>
            <BlockStack gap="400">
              <Text variant="bodyMd" tone="subdued">
                Detailed view of all commission adjustments applied in the selected time period.
              </Text>

              {adjustmentRows.length > 0 ? (
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text', 'numeric', 'text', 'text']}
                  headings={adjustmentTableHeadings}
                  rows={adjustmentRows}
                  pagination={{
                    hasNext: false,
                    hasPrevious: false,
                  }}
                />
              ) : (
                <Box padding="400">
                  <Text variant="bodyMd" tone="subdued" alignment="center">
                    No adjustment details available for the selected period.
                  </Text>
                </Box>
              )}

              <Box paddingBlockStart="400">
                <InlineStack align="space-between">
                  <Text variant="bodyMd">
                    <strong>Total Adjustments: {formatCurrency(totalAdjustmentAmount)}</strong>
                  </Text>
                  <Button onClick={() => setShowDetailsModal(false)}>
                    Close
                  </Button>
                </InlineStack>
              </Box>
            </BlockStack>
          </Modal.Section>
        </Modal>
      )}
    </>
  );
}

// Export preset variants for different use cases
export const CompactCommissionBreakdownCard = (props) => (
  <CommissionBreakdownCard
    tooltip="Commission breakdown: base commission + adjustments = total"
    {...props}
  />
);